import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';

import OBSWebSocket from 'obs-websocket-js';

import SocketListener from 'components/SocketListener';
import GlobalContext from 'contexts';
import GlobalStore from 'store';
import { getTime } from 'utils';

const store = new GlobalStore();
const socket = new OBSWebSocket();

const clampNumber = (n, min, max) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};

const safeBool = value => Boolean(value);

const SendCurrent = data => {
  // Update the store's current game information based on the incoming WebSocket data
  store.currentGame.title = data.title || 'Default Title';
  store.currentGame.subtitle = data.subtitle || 'Game Subtitle';
  store.currentGame.category = data.category || 'Default Category';
  store.currentGame.platform = data.platform || 'Default Platform';
  store.currentGame.extra = data.extrainfo || '';
  store.currentGame.estimate = data.estimate || '00:00:00';
};

const SendNext = data => {
  // Update the store's next game information based on the incoming WebSocket data
  store.nextGame[0].title = data.title || 'Default Title';
  store.nextGame[0].subtitle = data.subtitle || 'Game Subtitle';
  store.nextGame[0].category = data.category || 'Default Category';
  store.nextGame[0].platform = data.platform || 'Default Platform';
  store.nextGame[0].extra = data.extrainfo || '';
  store.nextGame[0].estimate = data.estimate || '00:00:00';
};

const TimerStart = data => {
  store.startTimer;
};

const TimerEnd = data => {
  store.endTimer;
};

const TimerContinue = data => {
  store.continueTimer;
};

const PlayerContinue = data => {
  store.runners[data.idx].endTime = null;
};

const PlayerStop = async data => {
  store.runners[data.idx].endTime = await getTime();
};

React.useLayoutEffect = React.useEffect;

const HEARTBEAT_INTERVAL_MS =
  typeof process.env.NEXT_PUBLIC_OBS_HEARTBEAT_INTERVAL_MS !== 'undefined'
    ? Number(process.env.NEXT_PUBLIC_OBS_HEARTBEAT_INTERVAL_MS)
    : 30_000;

const EVENT_SUBSCRIPTIONS = 2047 | (1 << 16);

const RECONNECT_BACKOFF_MS = [2000, 4000, 8000, 16000, 30000];
const MAX_RECONNECT_ATTEMPTS = 5;

const GlobalContextProvider = ({ children }) => {
  useRouter();

  store.obs.socket = socket;

  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    const obs = store.obs.socket;

    if (!obs) {
      return;
    }

    const clearHeartbeat = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };

    const clearReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptRef.current = 0;
    };

    const tryReconnect = () => {
      const address = store.test?.address;
      const password = store.test?.password;
      if (!address || reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }
      const attempt = reconnectAttemptRef.current;
      reconnectAttemptRef.current += 1;
      const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        obs
          .connect(address, password || null, { eventSubscriptions: EVENT_SUBSCRIPTIONS })
          .then(() => {
            reconnectAttemptRef.current = 0;
          })
          .catch(() => {
            tryReconnect();
          });
      }, delay);
    };

    store.obs.actions = {
      getInputMute: async inputName => {
        const res = await obs.call('GetInputMute', { inputName });
        return safeBool(res?.inputMuted);
      },
      setInputMute: async (inputName, inputMuted) => {
        return obs.call('SetInputMute', { inputName, inputMuted: safeBool(inputMuted) });
      },
      getInputVolume: async inputName => {
        const res = await obs.call('GetInputVolume', { inputName });
        return res || {};
      },
      setInputVolume: async (inputName, inputVolumeMul) => {
        const mul = clampNumber(inputVolumeMul, 0, 20);
        await obs.call('SetInputVolume', { inputName, inputVolumeMul: mul });
      },
    };

    const refreshData = () => {
      const cameraSceneName = '!CAMERAS';
      // Scenes
      obs.call('GetSceneList').then(data => {
        store.obs.scenes = data.scenes.map(s => s.sceneName).filter(s => !s.includes('!'));
        store.obs.currentScene = data.currentProgramSceneName;
        store.obs.previewScene = data.currentPreviewSceneName;
      });

      //Sources
      obs.call('GetInputList').then(data => {
        // console.log('Sources: ', toJS(data));
        store.obs.inputs = data.inputs.map(s => s.inputName);
        store.obs.groupa = data.inputs.map(s => s.inputName).filter(s => s.includes('Room A'));
        store.obs.groupb = data.inputs.map(s => s.inputName).filter(s => s.includes('Room B'));

        store.obs.hosta = data.inputs
          .map(s => s.inputName)
          .filter(s => s.includes('Ninja A - Host'));
        store.obs.hostb = data.inputs
          .map(s => s.inputName)
          .filter(s => s.includes('Ninja B - Host'));

        store.obs.ads = data.inputs
          .map(s => s.inputName)
          .filter(s => s.includes('AD :'))
          .reverse();

        for (let j = 0; j < store.obs.rtmp.length; j++) {
          const inputName = store.obs.rtmp[j].name;
          if (!store.hasInput(inputName)) {
            store.obs.rtmp[j].muted = true;
            continue;
          }
          obs.call('GetInputMute', { inputName }).then(muteData => {
            store.obs.rtmp[j].muted = muteData.inputMuted;
          });
        }

        obs.call('GetSpecialInputs').then(special => {
          const audioNames = [
            special.desktop1,
            special.desktop2,
            special.mic1,
            special.mic2,
            special.mic3,
            special.mic4,
          ].filter(Boolean);
          const uniqueNames = [...new Set(audioNames)];
          store.obs.audio = uniqueNames.map(name => ({ name, muted: false }));

          for (let i = 0; i < store.obs.audio.length; i++) {
            const inputName = store.obs.audio[i].name;
            if (!store.hasInput(inputName)) {
              store.obs.audio[i].muted = true;
              continue;
            }
            obs.call('GetInputMute', { inputName }).then(muteData => {
              store.obs.audio[i].muted = muteData.inputMuted;
            });
          }
        });
      });

      // Cameras (scene: !CAMERAS, type: Video Capture Device)
      obs
        .call('GetSceneItemList', { sceneName: cameraSceneName })
        .then(data => {
          const sceneItems = data.sceneItems || [];
          const cameraItems = sceneItems.filter(item => {
            const kind = String(item.sourceKind || item.sourceType || '').toLowerCase();
            return (
              kind === 'video capture device' ||
              kind === 'video_capture_device' ||
              kind === 'dshow_input'
            );
          });
          const items = cameraItems.length > 0 ? cameraItems : sceneItems;
          store.obs.cam = items.map(item => ({
            name: item.sourceName,
            visible: Boolean(item.sceneItemEnabled),
          }));
          // Run visibility refresh only after store.obs.cam is set to avoid race with async callbacks
          for (let k = 0; k < store.obs.cam.length; k++) {
            const idx = k;
            store.getSceneItemEnabled(cameraSceneName, store.obs.cam[k].name).then(enabled => {
              if (enabled !== null && store.obs.cam[idx] != null) {
                store.obs.cam[idx].visible = enabled;
              }
            });
          }
        })
        .catch(() => {
          store.obs.cam = [];
        });

      // Backgrounds (scene: !BACKGROUNDS) — source names for Background/Frame dropdowns
      obs
        .call('GetSceneItemList', { sceneName: '!BACKGROUNDS' })
        .then(data => {
          const sceneItems = data.sceneItems || [];
          store.backgroundSourceNames = sceneItems.map(item => item.sourceName);
        })
        .catch(() => {
          store.backgroundSourceNames = [];
        });

      // Preview Scene
      obs.call('GetCurrentPreviewScene').then(data => {
        store.obs.previewScene = data.sceneName;
      });

      // Transitions
      obs.call('GetSceneTransitionList').then(data => {
        store.obs.transitions = data.transitions.map(t => t.transitionName);
        store.obs.currentTransition = data.currentSceneTransitionName;
      });

      if (store.obs.ads !== undefined) {
        for (let l = 0; l < store.obs.ads.length; l++) {
          const idx = l;
          store.getSceneItemEnabled('!Ads', store.obs.ads[l]).then(enabled => {
            if (enabled !== null && store.obs.ad[idx] != null) {
              store.obs.ad[idx].visible = enabled;
            }
          });
        }
      }
    };

    // Hydrate the store only after identification completes
    obs.on('Identified', () => {
      store.test.connected = true;
      store.obs.sceneItemIdCache = {};
      clearReconnect();
      clearHeartbeat();
      heartbeatIntervalRef.current = setInterval(() => {
        obs.call('GetVersion').catch(() => {
          store.test.connected = false;
          clearHeartbeat();
        });
      }, HEARTBEAT_INTERVAL_MS);
      obs.call('GetStudioModeEnabled').then(data => {
        if (!data.studioModeEnabled) {
          obs.call('SetStudioModeEnabled', { studioModeEnabled: true });
        }
      });
      refreshData();
    });

    obs.on('ConnectionClosed', () => {
      store.test.connected = false;
      clearHeartbeat();
      tryReconnect();
    });

    obs.on('CurrentProgramSceneChanged', data => {
      store.obs.currentScene = data.sceneName;
      refreshData();
      obs.call('GetCurrentProgramScene').then(sceneData => {
        store.obs.currentScene = sceneData.sceneName;
      });
    });

    // obs.on('MediaStarted', data => {
    //   console.log('Ad Started ...');
    //   if (data.sourceName.includes('AD')) {
    //     store.setAdIndex(null);
    //   }
    // });

    // obs.on('MediaEnded', data => {
    //   console.log('Ad Ended ...');
    //   //obs.send('SetCurrentTransition', { 'transition-name': 'Fade' });
    //   if (data.sourceName.includes('AD')) {
    //     let name = 'Fade';
    //     obs.send('TransitionToProgram', { 'with-transition': { name } });
    //   }
    // });

    obs.on('CurrentPreviewSceneChanged', data => {
      store.obs.previewScene = data.sceneName;
    });

    obs.on('CurrentSceneCollectionChanged', () => {
      store.obs.sceneItemIdCache = {};
      refreshData();
    });

    obs.on('CustomEvent', data => {
      const payload = data?.eventData || {};
      switch (payload.eventType) {
        case 'UCISendCurrent':
          SendCurrent(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCISendNext':
          SendNext(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCITimerStart':
          TimerStart(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCITimerEnd':
          TimerEnd(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCITimerContinue':
          TimerContinue(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCIPlayerContinue':
          PlayerContinue(payload);
          return { message: 'Successfully updated current game', status: 200 };
        case 'UCIPlayerStop':
          PlayerStop(payload);
          return { message: 'Successfully updated current game', status: 200 };
        default:
          return { message: 'Command not found.', status: 404 };
      }
    });

    return () => {
      clearHeartbeat();
      clearReconnect();
    };
  }, [store.obs.socket]);

  return (
    <GlobalContext.Provider value={store}>
      <SocketListener>{children}</SocketListener>
    </GlobalContext.Provider>
  );
};

GlobalContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default observer(GlobalContextProvider);
